import { useEffect, useState } from "react";
import { Download, FileText, MessageCircle, Loader2 } from "lucide-react";
import { useAuth } from "@clerk/react";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { useAuthedGet, useAuthedHttp, errMsg, inr, waLink, http } from "@/lib/api";

export const ENQ_STATUSES = ["New", "Under Review", "Quotation Sent", "Approved", "In Production", "Completed"];
const COLOR = { New: "bg-neon text-black", "Under Review": "bg-yellow-400 text-black", "Quotation Sent": "bg-blue-500", Approved: "bg-emerald-500 text-black", "In Production": "bg-purple-500", Completed: "bg-white/20" };

const intl = (n) => { const d = (n || "").replace(/\D/g, ""); return d.length === 10 ? "91" + d : d; };

function PrivateFile({ f }) {
  const { getToken } = useAuth();
  const [url, setUrl] = useState(null);
  useEffect(() => {
    let u;
    (async () => {
      const r = await http.get(`/files/${f.path}`, { responseType: "blob", headers: { Authorization: `Bearer ${await getToken()}` } });
      u = URL.createObjectURL(r.data);
      setUrl(u);
    })().catch(() => toast.error(`Could not load ${f.name}`));
    return () => u && URL.revokeObjectURL(u);
  }, [f.path, f.name, getToken]);
  return (
    <div className="bg-black border border-white/10 p-2" data-testid="admin-artwork-file">
      <div className="aspect-square grid place-items-center overflow-hidden">
        {!url ? <Loader2 className="animate-spin" /> : f.content_type.startsWith("image/") ? <a href={url} target="_blank" rel="noreferrer"><img src={url} alt={f.name} className="max-h-full object-contain" /></a> : <a href={url} target="_blank" rel="noreferrer" className="flex flex-col items-center gap-2 text-xs"><FileText className="w-10 h-10" />Open PDF</a>}
      </div>
      <p className="text-[11px] truncate mt-2">{f.name}</p>
      {url && <a href={url} download={f.name} className="text-[11px] text-neon flex items-center gap-1 mt-1" data-testid="admin-artwork-download"><Download className="w-3 h-3" />Download original</a>}
    </div>
  );
}

function Detail({ e, onClose }) {
  const call = useAuthedHttp();
  const qc = useQueryClient();
  const [s, setS] = useState({ status: e.status, quote_amount: e.quote_amount ?? "", quote_note: e.quote_note || "" });
  const save = async () => {
    try {
      await call("patch", `/admin/enquiries/${e.id}`, { ...s, quote_amount: s.quote_amount === "" ? null : Number(s.quote_amount) });
      toast.success("Enquiry updated");
      qc.invalidateQueries({ queryKey: ["authed"] });
      onClose();
    } catch (er) { toast.error(errMsg(er)); }
  };
  const msg = `Hi ${e.full_name}, this is DEN & THREADS regarding enquiry ${e.enquiry_id} (${e.quantity} × ${e.product_type}).${s.quote_amount ? ` Our quotation: ${inr(s.quote_amount)}.` : ""} ${s.quote_note}`;
  const rows = [["Order type", e.order_type], e.business_name && ["Business", e.business_name], ["Name", e.full_name], ["Mobile", e.mobile], ["WhatsApp", e.whatsapp], ["Email", e.email], ["City", e.city], ["Product", e.product_type], ["Colour", e.garment_color], ["Placement", e.placement], ["Quantity", e.quantity], e.design_slug && ["Anime design", e.design_slug]].filter(Boolean);
  return (
    <div className="space-y-6" data-testid="admin-enquiry-detail">
      <div className="grid sm:grid-cols-2 gap-x-6 gap-y-2 text-sm">{rows.map(([k, v]) => <div key={k} className="flex justify-between border-b border-white/5 py-1"><span className="text-grey">{k}</span><span className="text-right">{v || "—"}</span></div>)}</div>
      {Object.keys(e.size_breakdown || {}).length > 0 && <div><span className="label">Size breakdown</span><div className="flex gap-2">{Object.entries(e.size_breakdown).map(([k, v]) => <span key={k} className="chip">{k}: {v}</span>)}</div></div>}
      {e.instructions && <div><span className="label">Instructions</span><p className="text-sm bg-black p-3">{e.instructions}</p></div>}
      <div><span className="label">Artwork ({e.files.length})</span><div className="grid grid-cols-2 sm:grid-cols-4 gap-2">{e.files.map((f) => <PrivateFile key={f.path} f={f} />)}</div></div>
      <div className="grid sm:grid-cols-3 gap-3 bg-[#171717] p-4">
        <label><span className="label">Status</span><select className="field" value={s.status} onChange={(x) => setS({ ...s, status: x.target.value })} data-testid="enquiry-status-select">{ENQ_STATUSES.map((t) => <option key={t}>{t}</option>)}</select></label>
        <label><span className="label">Quotation amount (₹)</span><input type="number" className="field" value={s.quote_amount} onChange={(x) => setS({ ...s, quote_amount: x.target.value })} data-testid="enquiry-quote-input" /></label>
        <label><span className="label">Quote note</span><input className="field" value={s.quote_note} onChange={(x) => setS({ ...s, quote_note: x.target.value })} data-testid="enquiry-quote-note-input" /></label>
        <div className="sm:col-span-3 flex flex-wrap gap-2">
          <button onClick={save} className="btn-neon py-3" data-testid="enquiry-save-button">Save</button>
          <a href={waLink(intl(e.whatsapp || e.mobile), msg)} target="_blank" rel="noreferrer" className="btn-ghost py-3" data-testid="enquiry-whatsapp-button"><MessageCircle className="w-4 h-4" />Send quote on WhatsApp</a>
          <a href={`mailto:${e.email}?subject=Quotation ${e.enquiry_id}&body=${encodeURIComponent(msg)}`} className="btn-ghost py-3">Email</a>
        </div>
      </div>
    </div>
  );
}

export function Enquiries({ kind }) {
  const [status, setStatus] = useState("");
  const { data = [], isLoading } = useAuthedGet(`/admin/enquiries?kind=${kind}&status=${encodeURIComponent(status)}`);
  const [open, setOpen] = useState(null);
  return (
    <div data-testid={`admin-enquiries-${kind}`}>
      <h1 className="font-display text-4xl mb-6">{kind === "custom" ? "CUSTOM DESIGN ENQUIRIES" : "WHOLESALE ENQUIRIES"}</h1>
      <div className="flex flex-wrap gap-2 mb-6">{["", ...ENQ_STATUSES].map((s) => <button key={s || "all"} onClick={() => setStatus(s)} className={`chip ${status === s ? "on" : ""}`} data-testid={`enq-filter-${s || "all"}`}>{s || "All"}</button>)}</div>
      {isLoading ? <Loader2 className="animate-spin" /> : (
        <div className="overflow-x-auto border border-white/10">
          <table className="w-full text-sm">
            <thead className="bg-[#171717] text-left font-mono text-[11px] uppercase text-grey"><tr>{["ID", kind === "wholesale" ? "Business" : "Customer", "Product", "Qty", "Files", "Quote", "Status", "Date"].map((h) => <th key={h} className="p-3">{h}</th>)}</tr></thead>
            <tbody>
              {data.map((e) => (
                <tr key={e.id} onClick={() => setOpen(e)} className="border-t border-white/10 hover:bg-white/[0.03] cursor-pointer" data-testid={`enquiry-row-${e.enquiry_id}`}>
                  <td className="p-3 font-mono text-neon">{e.enquiry_id}</td>
                  <td className="p-3">{e.business_name || e.full_name}<p className="text-xs text-grey">{e.mobile}</p></td>
                  <td className="p-3">{e.product_type}<p className="text-xs text-grey">{e.placement}</p></td>
                  <td className="p-3 font-mono">{e.quantity}</td>
                  <td className="p-3 font-mono">{e.files.length}</td>
                  <td className="p-3 font-mono">{e.quote_amount != null ? inr(e.quote_amount) : "—"}</td>
                  <td className="p-3"><span className={`text-[10px] font-mono px-2 py-1 ${COLOR[e.status]}`}>{e.status}</span></td>
                  <td className="p-3 text-xs text-grey">{new Date(e.created_at).toLocaleDateString()}</td>
                </tr>
              ))}
              {!data.length && <tr><td colSpan={8} className="p-8 text-center text-grey">No enquiries.</td></tr>}
            </tbody>
          </table>
        </div>
      )}
      <Dialog open={!!open} onOpenChange={(o) => !o && setOpen(null)}>
        <DialogContent className="bg-[#111] border-white/10 max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogTitle className="font-display text-3xl">ENQUIRY {open?.enquiry_id}</DialogTitle>
          {open && <Detail e={open} onClose={() => setOpen(null)} />}
        </DialogContent>
      </Dialog>
    </div>
  );
}
